import React from 'react';

import { reverse } from 'named-urls';

import { Link } from 'react-router-dom';

import routes from 'core/routes';

import { NotificationTarget } from 'core/constants';

import { getLinkForNotification } from 'containers/KnockNotifications/constants';

import { GoToLink, EntityName, Text } from './styles';
import { NotificationProps } from './types';

const Notification: React.FunctionComponent<NotificationProps> = ({ notification, disableLink = false }) => {
  const renderTarget = (to: string, label: React.ReactNode): React.ReactElement =>
    disableLink ? (
      <EntityName>{label}</EntityName>
    ) : (
      <GoToLink to={to} as={Link}>
        {label}
      </GoToLink>
    );

  switch (notification.target) {
    case NotificationTarget.NEW_INCIDENT_MESSAGE:
    case NotificationTarget.NEW_INCIDENT_SUBMISSION_MESSAGE:
      return (
        <Text>
          {notification.dismissed ? notification.creator.name : <b>{notification.creator.name}</b>}
          {' mentioned you in the incident: '}
          {renderTarget(
            reverse(routes.dashboard.events.details, { entityId: notification.instance.uuid }),
            notification.instance.subject
          )}
        </Text>
      );
    case NotificationTarget.NEW_TASK_MESSAGE:
      return (
        <Text>
          {notification.dismissed ? notification.creator.name : <b>{notification.creator.name}</b>}
          {' mentioned you in the action: '}
          {renderTarget(
            reverse(routes.dashboard.tasks.details, { taskId: notification.instance.uuid }),
            notification.instance.name
          )}
        </Text>
      );
    case NotificationTarget.NEW_TASK_ASSIGNED_TO_ME:
      return (
        <Text>
          {notification.dismissed ? notification.creator.name : <b>{notification.creator.name}</b>}
          {' assigned you an action: '}
          {renderTarget(
            reverse(routes.dashboard.tasks.details, { taskId: notification.instance.uuid }),
            notification.instance.name
          )}
        </Text>
      );
    case NotificationTarget.NEW_INCIDENT_REPORTED:
      return (
        <Text>
          {notification.dismissed ? notification.creator.name : <b>{notification.creator.name}</b>}
          {' reported a new incident: '}
          {renderTarget(
            reverse(routes.dashboard.events.details, { entityId: notification.instance.uuid }),
            notification.instance.subject
          )}
        </Text>
      );
    case NotificationTarget.NEW_INCIDENT_ASSIGNED_TO_ME:
      return (
        <Text>
          {notification.dismissed ? notification.creator.name : <b>{notification.creator.name}</b>}
          {' assigned you an incident: '}
          {renderTarget(
            reverse(routes.dashboard.events.details, { entityId: notification.instance.uuid }),
            notification.instance.subject
          )}
        </Text>
      );
    case NotificationTarget.NEW_FORM_SUBMITTED:
      return (
        <Text>
          {notification.dismissed ? notification.creator.name : <b>{notification.creator.name}</b>}
          {' submitted a new form: '}
          {renderTarget(
            reverse(routes.dashboard.audits.forms.details, { formId: notification.instance.uuid }),
            notification.instance.name
          )}
        </Text>
      );

    case NotificationTarget.AUDIT_COMMENT_MENTION:
      return (
        <Text>
          {notification.dismissed ? notification.creator.name : <b>{notification.creator.name}</b>}
          {' mentioned you in the audit: '}
          {renderTarget(getLinkForNotification(notification), notification.instance.name)}
        </Text>
      );

    case NotificationTarget.NEW_EVENT_REPORTED:
      if (notification.creator.name) {
        return (
          <Text>
            {notification.dismissed ? notification.creator.name : <b>{notification.creator.name}</b>}
            {' submitted a new event: '}
            {renderTarget(
              reverse(routes.dashboard.events.details, { entityId: notification.instance.uuid }),
              notification.instance.name
            )}
          </Text>
        );
      }

      return (
        <Text>
          {'New event submitted: '}
          {renderTarget(
            reverse(routes.dashboard.events.details, { entityId: notification.instance.uuid }),
            notification.instance.name
          )}
        </Text>
      );

    case NotificationTarget.NEW_EVENT_ASSIGNED_TO_ME:
      return (
        <Text>
          {notification.dismissed ? notification.creator.name : <b>{notification.creator.name}</b>}
          {' assigned you an event: '}
          {renderTarget(
            reverse(routes.dashboard.events.details, { entityId: notification.instance.uuid }),
            notification.instance.name
          )}
        </Text>
      );

    case NotificationTarget.NEW_EVENT_MESSAGE:
      return (
        <Text>
          {notification.dismissed ? notification.creator.name : <b>{notification.creator.name}</b>}
          {' mentioned you in the event: '}
          {renderTarget(
            reverse(routes.dashboard.events.details, { entityId: notification.instance.uuid }),
            notification.instance.name
          )}
        </Text>
      );

    case NotificationTarget.EVENT_COLLABORATED:
      return (
        <Text>
          {'You’ve been added as a collaborator on this event: '}
          {renderTarget(
            reverse(routes.dashboard.events.details, { entityId: notification.instance.uuid }),
            notification.instance.name
          )}
        </Text>
      );

    case NotificationTarget.EVENT_STAGE_CHANGED:
      return (
        <Text>
          {'Event stage changed: '}
          {renderTarget(
            reverse(routes.dashboard.events.details, { entityId: notification.instance.uuid }),
            notification.instance.name
          )}
        </Text>
      );

    case NotificationTarget.EVENT_COMMENT:
      return (
        <Text>
          {notification.dismissed ? notification.creator.name : <b>{notification.creator.name}</b>}
          {' left a comment on event: '}
          {renderTarget(
            reverse(routes.dashboard.events.details, { entityId: notification.instance.uuid }),
            notification.instance.name
          )}
        </Text>
      );

    case NotificationTarget.NEW_ASSET_MESSAGE:
      return (
        <Text>
          {notification.dismissed ? notification.creator.name : <b>{notification.creator.name}</b>}
          {' mentioned you in the asset: '}
          {renderTarget(
            reverse(routes.dashboard.assets.details, { entityId: notification.instance.uuid }),
            notification.instance.name
          )}
        </Text>
      );

    case NotificationTarget.NEW_SITE_MESSAGE:
      return (
        <Text>
          {notification.dismissed ? notification.creator.name : <b>{notification.creator.name}</b>}
          {' mentioned you in the site: '}
          {renderTarget(
            reverse(routes.dashboard.sites.details, { entityId: notification.instance.uuid }),
            notification.instance.name
          )}
        </Text>
      );

    case NotificationTarget.NEW_SITE_ASSIGNED_TO_ME:
      return (
        <Text>
          {notification.dismissed ? notification.creator.name : <b>{notification.creator.name}</b>}
          {' assigned you a site: '}
          {renderTarget(
            reverse(routes.dashboard.sites.details, { entityId: notification.instance.uuid }),
            notification.instance.name
          )}
        </Text>
      );

    case NotificationTarget.NEW_SCHEDULE_ASSIGNED_TO_ME:
      return (
        <Text>
          {notification.dismissed ? notification.creator.name : <b>{notification.creator.name}</b>}
          {' assigned you an audit: '}
          {renderTarget(routes.dashboard.tasks.audits.toString(), notification.instance.name)}
        </Text>
      );
    case NotificationTarget.ACKNOWLEDGEMENT_ASSIGNED_TO_ME:
      return (
        <Text>
          {notification.dismissed ? notification.creator.name : <b>{notification.creator.name}</b>}
          {' asked you to acknowledge: '}
          {renderTarget(
            reverse(routes.dashboard.acknowledgements.details, { acknowledgementId: notification.instance.uuid }),
            notification.instance.name
          )}
        </Text>
      );
    case NotificationTarget.DOCUMENT_COMMENT_MENTION:
      return (
        <Text>
          {notification.dismissed ? notification.creator.name : <b>{notification.creator.name}</b>}
          {' mentioned you in the document: '}
          {renderTarget(
            reverse(routes.dashboard.documents.details, { entityId: notification.instance.uuid }),
            notification.instance.name
          )}
        </Text>
      );

    case NotificationTarget.RISK_COMMENT_MENTION:
      return (
        <Text>
          {notification.dismissed ? notification.creator.name : <b>{notification.creator.name}</b>}
          {' mentioned you in a risk: '}
          {renderTarget(
            reverse(routes.dashboard.risks.details, { entityId: notification.instance.uuid }),
            notification.instance.name
          )}
        </Text>
      );
    case NotificationTarget.RISK_ACTION_ASSIGNED_TO_ME:
      return (
        <Text>
          {notification.creator.name ? (
            <>
              {notification.dismissed ? notification.creator.name : <b>{notification.creator.name}</b>}
              {' assigned you a risk action: '}
            </>
          ) : (
            'A risk action was assigned to you: '
          )}
          {renderTarget(getLinkForNotification(notification), notification.instance.name)}
        </Text>
      );
    case NotificationTarget.TASK_DUE_TODAY:
    case NotificationTarget.AUDIT_DUE_TODAY:
    case NotificationTarget.RISK_ACTION_DUE_TODAY:
      // System-generated (no actor): lead with the record name, not a creator.
      return (
        <Text>
          {renderTarget(getLinkForNotification(notification), notification.instance.name)}
          {' is due today'}
        </Text>
      );
    case NotificationTarget.ACKNOWLEDGEMENT_DUE_TODAY:
      return (
        <Text>
          {'Your acknowledgement of '}
          {renderTarget(getLinkForNotification(notification), notification.instance.name)}
          {' is due today'}
        </Text>
      );
    default:
      return (
        <Text>
          <b>{notification.creator.name}</b>
          {' Unknown notification'}
        </Text>
      );
  }
};

export default Notification;
